import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import { announcementService } from '../../services/announcementService';
import { useNavigate } from 'react-router-dom';

const AnnouncementBanner = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnnouncements();
    
    // Refresh announcements every 30 seconds to pick up admin changes
    const refreshInterval = setInterval(() => {
      loadAnnouncements();
    }, 30000);
    
    return () => clearInterval(refreshInterval);
  }, []);

  useEffect(() => {
    if (announcements?.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % announcements?.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [announcements?.length]);

  const loadAnnouncements = async () => {
    try {
      const data = await announcementService?.getPublishedAnnouncements();
      // console.log('AnnouncementBanner - Loaded announcements:', data);
      setAnnouncements(data);
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCTAClick = (url) => {
    if (url?.startsWith('http')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  if (loading || dismissed || announcements?.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements?.[currentIndex];
  // console.log('AnnouncementBanner - Current announcement:', currentAnnouncement);

  return (
    <div className="w-full bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-950/20 dark:via-indigo-950/20 dark:to-purple-950/20 border-b border-border/50">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-8 py-3">
        <div className="flex items-center gap-3 md:gap-4">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
              <Icon name="Megaphone" size={18} className="text-white" />
            </div>
          </div>

          {/* Content - Flexible layout */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-sm md:text-base leading-tight mb-0.5">
              {currentAnnouncement?.title || 'Announcement'}
            </h3>
            {currentAnnouncement?.content && (
              <p className="text-xs md:text-sm text-foreground/70 leading-snug">
                {currentAnnouncement?.content}
              </p>
            )}
          </div>

          {/* Media Preview - Hidden on mobile if space constrained */}
          {currentAnnouncement?.fileUrl && currentAnnouncement?.fileType?.startsWith('image/') && (
            <div className="hidden sm:block flex-shrink-0">
              <img
                src={currentAnnouncement?.fileUrl}
                alt={currentAnnouncement?.title || 'Announcement image'}
                className="h-16 w-20 md:h-20 md:w-24 object-cover rounded-md border border-border/50"
              />
            </div>
          )}

          {/* CTA Button */}
          {currentAnnouncement?.ctaButtonText && currentAnnouncement?.ctaButtonUrl && (
            <div className="flex-shrink-0">
              <Button
                size="sm"
                onClick={() => handleCTAClick(currentAnnouncement?.ctaButtonUrl)}
                className="bg-primary hover:bg-primary/90 text-white text-xs md:text-sm font-medium shadow-sm hover:shadow-md transition-all whitespace-nowrap px-3 py-1.5"
              >
                {currentAnnouncement?.ctaButtonText}
                <Icon name="ArrowRight" size={14} className="ml-1" />
              </Button>
            </div>
          )}

          {/* Controls */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* Pagination Dots */}
            {announcements?.length > 1 && (
              <div className="hidden md:flex items-center gap-1">
                {announcements?.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`transition-all duration-300 rounded-full ${
                      index === currentIndex
                        ? 'w-5 h-1.5 bg-primary' :'w-1.5 h-1.5 bg-border hover:bg-muted-foreground/40'
                    }`}
                    aria-label={`View announcement ${index + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Dismiss Button */}
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-muted/50 rounded transition-colors"
              aria-label="Dismiss announcement"
            >
              <Icon name="X" size={14} className="text-muted-foreground hover:text-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementBanner;